import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import csv from "csvtojson";
import {
  CircularLoader,
  Icon,
  Modal,
  Select,
  Stack,
  Title,
  useTheme,
  useToast
} from "@introist/react-foundation/v2";
import { FileUpload } from "../../../workflows/components/FileUpload/FileUpload";
import { CollapseList, ListRow } from "../../../../components/organisms";
import { useEmployees } from "../../../employees/hooks/useEmployees";
import { useEmployeeFields } from "../../../employees/hooks/useEmployeeFields";
import { useBatchActions } from "../../useBatches";

type Step = "upload" | "map" | "verify";

type MatchedRow = {
  csvValue: string;
  employeeId: string;
  employeeName: string;
};

type UnmatchedRow = {
  csvValue: string;
};

export const AddCsvModal = ({
  batchId,
  open,
  onClose
}: {
  batchId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const toast = useToast();
  const { theme } = useTheme();

  const [step, setStep] = useState<Step>("upload");
  const [parsedRows, setParsedRows] = useState<Record<string, string>[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [selectedCsvColumn, setSelectedCsvColumn] = useState("");
  const [selectedFieldKey, setSelectedFieldKey] = useState("");
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [delimiter, setDelimiter] = useState<"," | ";">("," );
  const [file, setFile] = useState<File | null>(null);
  const [previewRowIndex, setPreviewRowIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [matchedOpen, setMatchedOpen] = useState(false);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);

  useEffect(() => {
    setStep("upload");
    setParsedRows([]);
    setCsvColumns([]);
    setSelectedCsvColumn("");
    setSelectedFieldKey("");
    setHowItWorksOpen(false);
    setDelimiter(",");
    setFile(null);
    setPreviewRowIndex(0);
    setPreviewOpen(false);
    setMatchedOpen(false);
    setUnmatchedOpen(false);
  }, [open]);

  const { employees } = useEmployees({ fields: ["*"] });
  const { fields } = useEmployeeFields();

  const { addEmployees } = useBatchActions();

  const activeFields = useMemo(() => (fields ?? []).filter(f => !f.archived), [fields]);

  const { matchedRows, unmatchedRows } = useMemo(() => {
    if (!employees || !selectedCsvColumn || !selectedFieldKey) {
      return { matchedRows: [] as MatchedRow[], unmatchedRows: [] as UnmatchedRow[] };
    }

    const matched: MatchedRow[] = [];
    const unmatched: UnmatchedRow[] = [];
    const usedEmployeeIds = new Set<string>();

    for (const row of parsedRows) {
      const csvValue = (row[selectedCsvColumn] ?? "").trim();
      if (!csvValue) continue;

      const candidate = employees.find(
        emp =>
          !usedEmployeeIds.has(emp.id ?? "") &&
          String(emp[selectedFieldKey] ?? "")
            .trim()
            .toLowerCase() === csvValue.toLowerCase()
      );

      if (candidate) {
        const empId = candidate.id ?? "";
        const empName = candidate.name ?? "";
        usedEmployeeIds.add(empId);
        matched.push({ csvValue, employeeId: empId, employeeName: empName });
      } else {
        unmatched.push({ csvValue });
      }
    }

    return { matchedRows: matched, unmatchedRows: unmatched };
  }, [employees, parsedRows, selectedCsvColumn, selectedFieldKey]);

  const parseFile = useCallback(
    async (f: File, delim: string) => {
      try {
        const rows: Record<string, string>[] = await f
          .text()
          .then(text => csv({ delimiter: delim }).fromString(text));
        if (rows.length === 0) {
          toast.error("CSV appears to be empty");
          return;
        }
        setParsedRows(rows);
        setCsvColumns(Object.keys(rows[0]));
      } catch {
        toast.error("Failed to parse CSV file");
      }
    },
    [toast]
  );

  const handleFileUploaded = useCallback(
    async (files: File[]) => {
      const f = files[0];
      setFile(f);
      await parseFile(f, delimiter);
    },
    [delimiter, parseFile]
  );

  // Re-parse when delimiter changes if a file is already loaded
  useEffect(() => {
    if (file) {
      setParsedRows([]);
      setCsvColumns([]);
      parseFile(file, delimiter);
    }
    // Only run when delimiter changes; file and parseFile are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delimiter]);

  const modalTitle =
    step === "upload"
      ? "Import employees from CSV"
      : step === "map"
      ? "Map CSV column to employee field"
      : "Review matches";

  const primaryButton = useMemo(() => {
    if (step === "upload") {
      return {
        title: "Continue",
        disabled: parsedRows.length === 0,
        onClick: () => setStep("map")
      };
    }
    if (step === "map") {
      return {
        title: "Continue",
        disabled: !selectedCsvColumn || !selectedFieldKey,
        onClick: () => setStep("verify")
      };
    }
    const addDisabled = matchedRows.length === 0;
    const addLabel =
      matchedRows.length === 0
        ? "No employees matched"
        : unmatchedRows.length > 0
        ? `Add ${matchedRows.length} employees (${unmatchedRows.length} unmatched)`
        : `Add ${matchedRows.length} employees`;

    return {
      title: addLabel,
      disabled: addDisabled,
      onClickWithLoading: async () => {
        await addEmployees(
          batchId,
          matchedRows.map(r => r.employeeId)
        );
        onClose();
      }
    };
  }, [
    step,
    parsedRows.length,
    selectedCsvColumn,
    selectedFieldKey,
    matchedRows,
    unmatchedRows,
    addEmployees,
    batchId,
    onClose
  ]);

  const secondaryButton = useMemo(() => {
    if (step === "upload") return { title: "Cancel", onClick: onClose };
    if (step === "map") return { title: "Back", onClick: () => setStep("upload") };
    return { title: "Back", onClick: () => setStep("map") };
  }, [step, onClose]);

  const isLoading = !employees || !fields;

  // CollapseList fires onToggleOpen twice per click (Header row + IconButton both call it).
  // This wrapper deduplicates calls within the same event loop tick.
  const pendingToggle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const makeToggle = useCallback((fn: () => void) => {
    return () => {
      if (pendingToggle.current !== null) return;
      fn();
      pendingToggle.current = setTimeout(() => {
        pendingToggle.current = null;
      }, 0);
    };
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      style={{ width: "560px", maxHeight: "80vh" }}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
    >
      {isLoading && <CircularLoader />}

      {!isLoading && step === "upload" && (
        <Stack vertical gap="large">
          <Stack justifyContent="space-between" alignItems="center">
            <Title variant="bold">Delimiter</Title>
            <Select
              size="small"
              options={[
                { key: ",", title: "Comma (,)" },
                { key: ";", title: "Semicolon (;)" }
              ]}
              value={delimiter}
              onSelect={opt => setDelimiter(opt.key as "," | ";")}
            />
          </Stack>

          <FileUpload
            icon="upload"
            title={
              parsedRows.length > 0
                ? `${parsedRows.length} rows loaded — drop a new file to replace`
                : "Drop CSV file here or click to browse"
            }
            restrictions={["CSV files only", "Max 5MB"]}
            maxSize={5 * 1024 * 1024}
            onFileUploaded={handleFileUploaded}
          />

          {parsedRows.length > 0 && (
            <Stack gap="small">
              <Icon name="check" />
              <Title>{parsedRows.length} rows detected</Title>
            </Stack>
          )}

          <CollapseList
            open={howItWorksOpen}
            onToggleOpen={makeToggle(() => setHowItWorksOpen(v => !v))}
            header={<Title variant="bold">How does CSV import work?</Title>}
          >
            <Stack vertical gap="small" style={{ padding: "var(--spacing-large)" }}>
              <Stack gap="small">
                <Title>1.</Title>
                <Title>
                  Prepare a CSV with a column that identifies your employees — for example, an email
                  address or employee ID.
                </Title>
              </Stack>
              <Stack gap="small">
                <Title>2.</Title>
                <Title>Upload the file and choose which CSV column contains the identifier.</Title>
              </Stack>
              <Stack gap="small">
                <Title>3.</Title>
                <Title>Choose which Introist employee field to match against.</Title>
              </Stack>
              <Stack gap="small">
                <Title>4.</Title>
                <Title>
                  Introist will preview the matches so you can review before adding anyone.
                </Title>
              </Stack>
              <Stack gap="small">
                <Title>5.</Title>
                <Title>
                  Rows that don't match any employee are shown as warnings — you can still add the
                  matched employees.
                </Title>
              </Stack>
            </Stack>
          </CollapseList>
        </Stack>
      )}

      {!isLoading && step === "map" && (
        <Stack vertical gap="xLarge">
          <CollapseList
            open={previewOpen}
            onToggleOpen={makeToggle(() => setPreviewOpen(v => !v))}
            header={
              <Stack justifyContent="space-between" style={{ width: "100%" }}>
                <Title variant="bold">
                  Preview ({parsedRows.length} row{parsedRows.length !== 1 ? "s" : ""})
                </Title>
                {previewOpen && (
                  <div onClick={e => e.stopPropagation()}>
                    <Select
                      size="small"
                      options={parsedRows
                        .slice(0, Math.min(parsedRows.length, 10))
                        .map((_, i) => ({ key: String(i), title: `Row ${i + 1}` }))}
                      value={String(previewRowIndex)}
                      onSelect={opt => setPreviewRowIndex(Number(opt.key))}
                    />
                  </div>
                )}
              </Stack>
            }
          >
            {csvColumns.map(col => (
              <ListRow key={col} gridTemplateColumns="1fr 1fr">
                <Title variant="bold">{col}</Title>
                <Title>{parsedRows[previewRowIndex]?.[col] ?? ""}</Title>
              </ListRow>
            ))}
          </CollapseList>

          <Stack vertical gap="small">
            <Title variant="bold">Which column identifies employees?</Title>
            <Select
              placeholder="Select CSV column"
              searchable
              options={csvColumns.map(col => ({ key: col, title: col }))}
              value={selectedCsvColumn}
              onSelect={opt => setSelectedCsvColumn(opt.key)}
              style={{ width: "100%" }}
            />
          </Stack>

          <Stack vertical gap="small">
            <Title variant="bold">Which employee field to match against?</Title>
            <Select
              placeholder="Select employee field"
              searchable
              options={activeFields.map(f => ({ key: f.key, title: f.title }))}
              value={selectedFieldKey}
              onSelect={opt => setSelectedFieldKey(opt.key)}
              style={{ width: "100%" }}
            />
          </Stack>
        </Stack>
      )}

      {!isLoading && step === "verify" && (
        <Stack vertical gap="medium">
          <CollapseList
            open={matchedOpen}
            onToggleOpen={makeToggle(() => setMatchedOpen(v => !v))}
            header={
              <Stack gap="small">
                <Icon name="check" />
                <Title variant="bold">Matched ({matchedRows.length})</Title>
              </Stack>
            }
          >
            {matchedRows.map(row => (
              <ListRow key={row.employeeId} gridTemplateColumns="1fr auto">
                <Title variant="bold">{row.employeeName}</Title>
                <Title style={{ color: "var(--palette-foreground-subdued)" }}>
                  {row.csvValue}
                </Title>
              </ListRow>
            ))}
          </CollapseList>

          {unmatchedRows.length > 0 && (
            <CollapseList
              open={unmatchedOpen}
              onToggleOpen={makeToggle(() => setUnmatchedOpen(v => !v))}
              header={
                <Stack gap="small">
                  <Icon name="warning" style={{ color: theme.palette.danger.default }} />
                  <Title variant="bold" style={{ color: theme.palette.danger.default }}>
                    Unmatched ({unmatchedRows.length})
                  </Title>
                </Stack>
              }
            >
              {unmatchedRows.map((row, i) => (
                <ListRow key={i} gridTemplateColumns="1fr">
                  <Title>{row.csvValue}</Title>
                </ListRow>
              ))}
            </CollapseList>
          )}
        </Stack>
      )}
    </Modal>
  );
};
